import React, { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';

// Error Boundary for lazy-loaded components
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Failed to load component:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Something went wrong</h2>
          <p className="mb-4">Failed to load this component. Please try refreshing the page.</p>
          <button 
            className="bg-primary text-white px-4 py-2 rounded-md"
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Lazy load components to avoid chunk loading issues
const TeacherDashboard = lazy(() => import('../components/teachers/TeacherDashboard'));
const ClassActivities = lazy(() => import('../components/teachers/ClassActivities'));
const StudentProgress = lazy(() => import('../components/teachers/StudentProgress'));
const MessageCenter = lazy(() => import('../components/teachers/MessageCenter'));
const TeacherProfile = lazy(() => import('../components/teachers/TeacherProfile'));
const TeacherSettings = lazy(() => import('../components/teachers/TeacherSettings'));
const NotFound = lazy(() => import('../pages/public/NotFound'));

// Import real components instead of ComingSoon placeholders
const StudentsComponent = lazy(() => import('../components/teachers/Students'));
const CalendarComponent = lazy(() => import('../components/teachers/Calendar'));
const AssignmentsComponent = lazy(() => import('../components/teachers/Assignments'));
const ResourcesComponent = lazy(() => import('../components/teachers/Resources'));
const ReportsComponent = lazy(() => import('../components/teachers/Reports'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex justify-center items-center h-64">
    <LoadingSpinner size="lg" />
  </div>
);

/**
 * Nested routes configuration for the teacher panel
 * Uses lazy loading with Suspense and ErrorBoundary for better error handling
 */
const TeacherRoutes = () => {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<TeacherDashboard />} />
          <Route path="/dashboard" element={<TeacherDashboard />} />
          <Route path="/classes" element={<ClassActivities />} />
          <Route path="/students" element={<StudentsComponent />} />
          <Route path="/assignments" element={<AssignmentsComponent />} />
          <Route path="/calendar" element={<CalendarComponent />} />
          <Route path="/resources" element={<ResourcesComponent />} />
          <Route path="/progress" element={<StudentProgress />} />
          <Route path="/messages" element={<MessageCenter />} />
          <Route path="/reports" element={<ReportsComponent />} />
          <Route path="/profile" element={<TeacherProfile />} />
          <Route path="/settings" element={<TeacherSettings />} />
          {/* Add other teacher-specific routes here */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

export default TeacherRoutes; 